import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { ArrowLeft, BarChart3, History } from 'lucide-react'
import SprintBoardSelector from '../components/SprintBoardSelector'
import SprintConfiguration from '../components/SprintConfiguration'
import SprintAnalysisProgress from '../components/SprintAnalysisProgress'
import SprintReportResults from '../components/SprintReportResults'
import SprintReportHistory from '../components/SprintReportHistory'
import { sprintReportService } from '../lib/sprintReportService'
import { SprintReportRecord } from '../lib/sprintReportService'

export type SprintDetails = {
  label: string
  description: string
  duration: string
  goal: string
  startDate?: Date
  endDate?: Date
}

export type SprintReport = {
  summary: {
    totalCards: number
    completedCards: number
    incompleteCards: number
    velocity: number
    burndownData: any[]
  }
  insights: {
    bottlenecks: string[]
    teamPerformance: any[]
    commonIssues: string[]
    improvementSuggestions: string[]
    sentimentAnalysis: {
      positive: number
      negative: number
      neutral: number
    }
  }
  retrospective: {
    whatWentWell: string[]
    whatWentWrong: string[]
    actionItems: string[]
    teamMorale: 'positive' | 'negative' | 'neutral'
    technicalDebt: 'low' | 'medium' | 'high'
  }
  analysisLog: {
    timestamp: string
    sprintLabel: string
    boardId: string
    dataFetching: {
      cardsFound: number
      listsFound: number
      commentsFetched: number
      attachmentsFetched: number
      membersFetched: number
      activityRecords: number
      errors: string[]
    }
    metricsCalculation: {
      totalCards: number
      completedCards: number
      completionRate: number
      velocity: number
      cycleTime: number
      leadTime: number
      scopeCreep: number
      listDistribution: Record<string, number>
      labelDistribution: Record<string, number>
    }
    insightsGeneration: {
      bottlenecks: string[]
      teamPerformance: any[]
      commonIssues: string[]
      improvementSuggestions: string[]
      sentimentAnalysis: {
        positive: number
        negative: number
        neutral: number
      }
    }
    retrospectiveGeneration: {
      whatWentWell: string[]
      whatWentWrong: string[]
      actionItems: string[]
      teamMorale: 'positive' | 'negative' | 'neutral'
      technicalDebt: 'low' | 'medium' | 'high'
    }
    processingTime: {
      dataFetching: number
      metricsCalculation: number
      insightsGeneration: number
      retrospectiveGeneration: number
      total: number
    }
  }
}

type WorkflowStep = 'select-board' | 'configure-sprint' | 'analyzing' | 'results' | 'history'

export default function SprintReport() {
  const [currentStep, setCurrentStep] = useState<WorkflowStep>('select-board')
  const [selectedBoard, setSelectedBoard] = useState<string>('')
  const [sprintDetails, setSprintDetails] = useState<SprintDetails | null>(null)
  const [report, setReport] = useState<SprintReport | null>(null)
  const navigate = useNavigate()

  const handleBoardSelect = (boardId: string) => {
    setSelectedBoard(boardId)
    setCurrentStep('configure-sprint')
  }

  const handleSprintConfigure = (details: SprintDetails) => {
    setSprintDetails(details)
    setCurrentStep('analyzing')
  }

  const handleAnalysisComplete = async (analysisReport: SprintReport) => {
    try {
      // Save the report to the database
      if (selectedBoard && sprintDetails) {
        await sprintReportService.saveReport(
          selectedBoard,
          sprintDetails.label,
          sprintDetails,
          analysisReport
        )
      }
      
      setReport(analysisReport)
      setCurrentStep('results')
    } catch (error) {
      console.error('Failed to save report:', error)
      // Still show the results even if saving failed
      setReport(analysisReport)
      setCurrentStep('results')
    }
  }

  const handleNewAnalysis = () => {
    setCurrentStep('select-board')
    setSelectedBoard('')
    setSprintDetails(null)
    setReport(null)
  }

  const handleViewHistory = () => {
    setCurrentStep('history')
  }

  const handleViewReport = (reportRecord: SprintReportRecord) => {
    setReport(reportRecord.report_data)
    setSprintDetails(reportRecord.sprint_details)
    setCurrentStep('results')
  }

  const handleBack = () => {
    switch (currentStep) {
      case 'configure-sprint':
        setCurrentStep('select-board')
        setSelectedBoard('')
        break
      case 'analyzing':
        setCurrentStep('configure-sprint')
        break
      case 'results':
        setCurrentStep('analyzing')
        break
      case 'history':
        setCurrentStep('select-board')
        break
    }
  }

  const getStepTitle = () => {
    switch (currentStep) {
      case 'select-board':
        return 'Select Trello Board'
      case 'configure-sprint':
        return 'Configure Sprint Analysis'
      case 'analyzing':
        return 'Analyzing Sprint Data'
      case 'results':
        return 'Sprint Report Results'
      case 'history':
        return 'Sprint Report History'
      default:
        return 'Sprint Report'
    }
  }

  const getStepDescription = () => {
    switch (currentStep) {
      case 'select-board':
        return 'Choose the board containing your sprint data'
      case 'configure-sprint':
        return 'Specify the sprint label and provide sprint details'
      case 'analyzing':
        return 'AI is analyzing your sprint data and generating insights'
      case 'results':
        return 'Review your comprehensive sprint report'
      case 'history':
        return 'View and manage your previous sprint reports'
      default:
        return ''
    }
  }

  return (
    <div className="max-w-4xl mx-auto p-6">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center space-x-4 mb-4">
          <button
            onClick={() => navigate('/dashboard')}
            className="flex items-center space-x-2 text-gray-600 hover:text-gray-800"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>Back to Dashboard</span>
          </button>
        </div>
        
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 bg-primary-100 rounded-lg flex items-center justify-center">
            <BarChart3 className="w-5 h-5 text-primary-600" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{getStepTitle()}</h1>
            <p className="text-gray-600">{getStepDescription()}</p>
          </div>
        </div>
      </div>

      {/* Progress Steps */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          {[
            { id: 'select-board', label: 'Select Board', icon: '📋' },
            { id: 'configure-sprint', label: 'Configure Sprint', icon: '⚙️' },
            { id: 'analyzing', label: 'Analyzing', icon: '🤖' },
            { id: 'results', label: 'Results', icon: '📊' }
          ].map((step, index) => (
            <div key={step.id} className="flex items-center">
              <div className={`flex items-center space-x-2 ${
                currentStep === step.id ? 'text-primary-600' : 
                ['select-board', 'configure-sprint', 'analyzing', 'results'].indexOf(currentStep) > 
                ['select-board', 'configure-sprint', 'analyzing', 'results'].indexOf(step.id) 
                  ? 'text-green-600' : 'text-gray-400'
              }`}>
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm ${
                  currentStep === step.id ? 'bg-primary-100 text-primary-600' :
                  ['select-board', 'configure-sprint', 'analyzing', 'results'].indexOf(currentStep) > 
                  ['select-board', 'configure-sprint', 'analyzing', 'results'].indexOf(step.id)
                    ? 'bg-green-100 text-green-600' : 'bg-gray-100 text-gray-400'
                }`}>
                  {step.icon}
                </div>
                <span className="text-sm font-medium">{step.label}</span>
              </div>
              {index < 3 && (
                <div className={`w-16 h-0.5 mx-4 ${
                  ['select-board', 'configure-sprint', 'analyzing', 'results'].indexOf(currentStep) > 
                  ['select-board', 'configure-sprint', 'analyzing', 'results'].indexOf(step.id)
                    ? 'bg-green-200' : 'bg-gray-200'
                }`} />
              )}
            </div>
          ))}
        </div>
        
        {/* History Button */}
        <div className="mt-4 flex justify-center">
          <button
            onClick={handleViewHistory}
            className="flex items-center space-x-2 px-4 py-2 text-sm border border-gray-300 rounded-md hover:bg-gray-50"
          >
            <History className="w-4 h-4" />
            <span>View Report History</span>
          </button>
        </div>
      </div>

      {/* Step Content */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        {currentStep === 'select-board' && (
          <SprintBoardSelector onBoardSelect={handleBoardSelect} />
        )}

        {currentStep === 'configure-sprint' && (
          <SprintConfiguration
            boardId={selectedBoard}
            onConfigure={handleSprintConfigure}
            onBack={handleBack}
          />
        )}

        {currentStep === 'analyzing' && sprintDetails && (
          <SprintAnalysisProgress
            boardId={selectedBoard}
            sprintDetails={sprintDetails}
            onComplete={handleAnalysisComplete}
            onBack={handleBack}
          />
        )}

        {currentStep === 'results' && report && (
          <SprintReportResults
            report={report}
            sprintDetails={sprintDetails!}
            onNewAnalysis={handleNewAnalysis}
          />
        )}

        {currentStep === 'history' && (
          <SprintReportHistory onViewReport={handleViewReport} />
        )}
      </div>
    </div>
  )
} 